import CalendarClockIcon from '@assets/icons/calendar-clock.svg';
import DeliveryIcon from '@assets/icons/delivery.svg';
import React from 'react';
import { AdditionalDetailsErrorMap } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-sales-portal-checkout-additional-inputs';
import { CheckoutPanelAdditionalDetails } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/checkout-panel-additional-details';
import { createStyles } from '@/contexts/theme-context';
import { formatMonthDayInTimezone, formatTimeslot } from '@/utils/date';
import { InputValueMap } from '@/utils/new-order';
import { OrderEntrySummaryData } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/loaders/use-sales-portal-order-summary-loader';
import { OrderSummaryAlerts } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/order-summary-alerts';
import { OrderSummaryCard } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/order-summary-card';
import { OrderSummaryInput } from '@domain/configuration/types';
import { OrderSummaryMetricRow } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-order-summary-metric-rows';
import { OrderValidation } from '@/experiences/sales-portal/domain/ordering/blocs/mutators/use-save-sales-portal-cart-mutator';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';
import { View } from 'react-native';

const ICON_SIZE = 24;

interface CheckoutPanelContentProps {
  additionalInputErrorMap: AdditionalDetailsErrorMap;
  additionalInputs: OrderSummaryInput[];
  additionalInputValueMap: InputValueMap;
  firstOrderAction?: JSX.Element;
  isLoading?: boolean;
  onAdditionalInputBlur: (id: string) => void;
  onAdditionalInputChange: (id: string, title: string, value: string) => void;
  onAdditionalInputClearError: (id: string) => void;
  orderAction?: JSX.Element;
  orderEntrySummary?: OrderEntrySummaryData;
  orderValidation: OrderValidation | null;
  savedSelectedFulfillment: SavedSelectedFulfillment | null;
  selectedFulfillmentDisplay: string;
  splitOrderMetricRows: OrderSummaryMetricRow[][];
  timezones: TimeZoneMap;
}

export const CheckoutPanelContent: React.FC<CheckoutPanelContentProps> = ({
  additionalInputErrorMap,
  additionalInputValueMap,
  additionalInputs,
  firstOrderAction,
  isLoading,
  splitOrderMetricRows,
  onAdditionalInputBlur,
  onAdditionalInputClearError,
  onAdditionalInputChange,
  orderEntrySummary,
  orderValidation,
  savedSelectedFulfillment,
  selectedFulfillmentDisplay,
  timezones,
}) => {
  const [styles, theme] = useStyles();

  const formattedDate = savedSelectedFulfillment?.date
    ? formatMonthDayInTimezone(
        savedSelectedFulfillment.date,
        timezones.restaurant,
      )
    : '';
  const formattedTimeslot = savedSelectedFulfillment?.timeslot
    ? formatTimeslot(savedSelectedFulfillment.timeslot, timezones, true)
    : '';
  const ICON_STROKE = theme.sk.color.icon.primary;

  const splitOrderMetrics = isLoading
    ? [null]
    : (orderEntrySummary?.splitOrderMetrics ?? []);

  return (
    <View style={styles.summaryContainer}>
      {splitOrderMetrics.map((metrics, index) => {
        const isFirstOrder = index === 0;
        const orderActionForCard = isFirstOrder ? firstOrderAction : null;
        const icon = isFirstOrder ? (
          <DeliveryIcon
            height={ICON_SIZE}
            stroke={ICON_STROKE}
            width={ICON_SIZE}
          />
        ) : (
          <CalendarClockIcon
            height={ICON_SIZE}
            stroke={ICON_STROKE}
            width={ICON_SIZE}
          />
        );

        const additionalDetails =
          !isLoading && isFirstOrder && additionalInputs.length > 0 ? (
            <CheckoutPanelAdditionalDetails
              errorMap={additionalInputErrorMap}
              inputs={additionalInputs}
              onChange={onAdditionalInputChange}
              onClearFieldError={onAdditionalInputClearError}
              onValidateField={onAdditionalInputBlur}
              valueMap={additionalInputValueMap}
            />
          ) : null;

        const cardFormattedDate = metrics?.fulfillmentDate
          ? formatMonthDayInTimezone(
              metrics.fulfillmentDate,
              timezones.restaurant,
            )
          : formattedDate;

        const cardFormattedTimeslot = isFirstOrder
          ? formattedTimeslot
          : undefined;

        return (
          <View key={index} style={styles.orderSummary}>
            {isFirstOrder && !isLoading ? (
              <OrderSummaryAlerts orderValidation={orderValidation} />
            ) : null}
            <OrderSummaryCard
              additionalDetailsElement={additionalDetails}
              formattedDate={cardFormattedDate}
              formattedTimeslot={cardFormattedTimeslot}
              icon={icon}
              isLoading={isLoading}
              metricRows={splitOrderMetricRows[index] ?? []}
              orderActionElement={orderActionForCard}
              selectedFulfillmentDisplay={selectedFulfillmentDisplay}
              subtotalDisplayString={metrics?.subtotalDisplayString ?? ''}
              unitCount={metrics?.unitCount ?? 0}
            />
          </View>
        );
      })}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  orderSummary: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSmall,
  },
  summaryContainer: {
    gap: theme.sk.spacing.spaceLarge,
  },
}));
